import React, { useEffect, useState } from "react"

import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogContentText from "@material-ui/core/DialogContentText"
import DialogTitle from "@material-ui/core/DialogTitle"
import TextField from "@material-ui/core/TextField"
import CircularProgress from "@material-ui/core/CircularProgress"

/**
 * Modal de confirmação por código de e-mail (2FA CLIENTEFIEL).
 *
 * props:
 *  - open: boolean
 *  - emailMascarado: string        e-mail já mascarado para exibição
 *  - loading: boolean              requisição em andamento (confirmar/reenviar)
 *  - erro: string                  mensagem de erro inline
 *  - segundosRestantesIniciais: number  cooldown vindo de um 429 no envio inicial
 *  - onConfirmar: (codigo) => void
 *  - onReenviar: () => void
 *  - onClose: () => void
 */
export default function DialogCodigoEmail({
    open,
    emailMascarado,
    loading = false,
    erro = "",
    segundosRestantesIniciais = 0,
    onConfirmar,
    onReenviar,
    onClose,
}) {
    const [codigo, setCodigo] = useState("")
    const [cooldown, setCooldown] = useState(0)

    // Limpa o campo ao (re)abrir o dialog.
    useEffect(() => {
        if (open) setCodigo("")
    }, [open])

    // Aplica o cooldown recebido do envio inicial (429).
    useEffect(() => {
        if (segundosRestantesIniciais > 0) setCooldown(segundosRestantesIniciais)
    }, [segundosRestantesIniciais])

    // Contador regressivo do botão "Reenviar".
    useEffect(() => {
        if (cooldown <= 0) return
        const id = setInterval(() => {
            setCooldown(s => (s <= 1 ? 0 : s - 1))
        }, 1000)
        return () => clearInterval(id)
    }, [cooldown])

    const confirmar = () => {
        if (codigo.trim().length === 0 || loading) return
        onConfirmar(codigo.trim().toUpperCase())
    }

    const reenviar = () => {
        if (cooldown > 0 || loading) return
        // Reinicia a escala padrão do cooldown enquanto o backend não responde.
        setCooldown(30)
        onReenviar()
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (loading) return
                if (reason === "backdropClick") return
                onClose()
            }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>Confirme seu acesso</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Enviamos um código de 6 caracteres para <b>{emailMascarado}</b>. Digite-o
                    abaixo para concluir o login.
                </DialogContentText>
                <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    autoFocus
                    label="Código"
                    value={codigo}
                    onChange={e => setCodigo(e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 6, style: { textTransform: "uppercase", letterSpacing: "0.3em" } }}
                    onKeyDown={e => {
                        if (e.key === "Enter") confirmar()
                    }}
                    error={Boolean(erro)}
                    helperText={erro || " "}
                    disabled={loading}
                />
                <Button
                    onClick={reenviar}
                    disabled={cooldown > 0 || loading}
                    color="primary"
                    size="small"
                >
                    {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={confirmar}
                    variant="contained"
                    color="primary"
                    disabled={loading || codigo.trim().length === 0}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
